import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileEdit, Landmark, TrendingUp, AlertCircle, Pencil, CalendarDays, RotateCcw, BarChart2, Clock, DollarSign, Activity, Flag, CheckCircle2, Users, LayoutList, SlidersHorizontal, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  PROJECT_ROLE_LABELS,
  PROJECT_ROLE_DISPLAY_ORDER,
  type ProjectRoleType,
  type ProjectRoleConfig,
  type CustomFieldType,
} from '@/types';
import { ProjectCreationSetup } from './project-creation-setup';
import CategoryManagement from './category-management';
import { BudgetTypeSelector } from '@/components/budget-configuration';
import { useBudgetConfiguration } from '@/hooks/use-budget-configuration';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { BudgetTrackingType } from '@/types/budget-configuration.types';
import { ProjectStatusLabels } from '@/components/settings/ProjectStatusLabels';
import { useSchedulingThresholds } from '@/hooks/use-scheduling-thresholds';
import { useBudgetHealthThresholds } from '@/hooks/use-budget-health-thresholds';
import { useProjectContentSettings } from '@/hooks/use-project-content-settings';
import { useProjectCustomFields, MAX_FIELDS } from '@/hooks/use-project-custom-fields';
import { appendAuditEntry } from '@/lib/audit-log-storage';
import { fetchProjectRoleConfigs, saveProjectRoleConfigs } from '@/lib/data-service';
import { getDataMode } from '@/lib/data-service';
import { useDataMode } from '@/contexts/DataModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function SettingsProject() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || 'project-creation';
  const { isDemoMode } = useDataMode();
  const { authenticatedUser } = useAuth();
  const changedBy = isDemoMode
    ? 'Demo User'
    : authenticatedUser?.name ?? authenticatedUser?.email ?? 'Unknown User';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  // ===== SCHEDULING THRESHOLDS STATE =====
  const {
    thresholds,
    hasUnsavedChanges: hasThresholdChanges,
    isSaving: isSavingThresholds,
    updateThreshold,
    saveThresholds,
    resetToDefaults,
  } = useSchedulingThresholds();

  // ===== BUDGET HEALTH THRESHOLDS STATE =====
  const {
    thresholds: budgetThresholds,
    hasUnsavedChanges: hasBudgetThresholdChanges,
    isSaving: isSavingBudgetThresholds,
    updateThreshold: updateBudgetThreshold,
    saveThresholds: saveBudgetThresholds,
    resetToDefaults: resetBudgetThresholds,
  } = useBudgetHealthThresholds();

  // ===== UTILISATION FORMULA STATE =====
  const [utilisationFormula, setUtilisationFormula] = useState<'actual_vs_budget' | 'actual_vs_allocated' | 'billable_vs_capacity'>('actual_vs_budget');
  const [hasUnsavedUtilisationChanges, setHasUnsavedUtilisationChanges] = useState(false);
  const [isSavingUtilisation, setIsSavingUtilisation] = useState(false);
  const [savedUtilisationFormula, setSavedUtilisationFormula] = useState<'actual_vs_budget' | 'actual_vs_allocated' | 'billable_vs_capacity'>('actual_vs_budget');

  const handleUtilisationFormulaChange = (value: 'actual_vs_budget' | 'actual_vs_allocated' | 'billable_vs_capacity') => {
    setUtilisationFormula(value);
    setHasUnsavedUtilisationChanges(value !== savedUtilisationFormula);
  };

  const handleSaveUtilisationFormula = async () => {
    setIsSavingUtilisation(true);
    await new Promise(resolve => setTimeout(resolve, 500)); // placeholder until backend is ready
    setSavedUtilisationFormula(utilisationFormula);
    setHasUnsavedUtilisationChanges(false);
    setIsSavingUtilisation(false);
    toast.success('Utilisation formula saved successfully.');
  };

  const utilisationFormulaOptions = [
    {
      value: 'actual_vs_budget' as const,
      label: 'Actual vs Budgeted Hours',
      description: 'Actual hours ÷ Budgeted hours × 100. The most common formula — measures how much of the hours budget has been consumed.',
    },
    {
      value: 'actual_vs_allocated' as const,
      label: 'Actual vs Allocated Hours',
      description: 'Actual hours ÷ Allocated hours × 100. Measures how much of the allocated (scheduled) capacity has been used.',
    },
    {
      value: 'billable_vs_capacity' as const,
      label: 'Billable Actuals vs Available Capacity',
      description: 'Billable actual hours ÷ Available capacity hours × 100. Used when tracking billable efficiency against total available time.',
    },
  ];

  // ===== BUDGET CATEGORIES STATE =====
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  // ===== KEY PEOPLE SETTINGS STATE =====
  // Defaults: all 8 roles enabled, only Project Manager required.
  const defaultRoleConfigs: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => ({
    role,
    enabled: true,
    required: role === 'project-manager',
    displayLabel: undefined,
  }));
  const [roleConfigs, setRoleConfigs] = useState<ProjectRoleConfig[]>(defaultRoleConfigs);
  const [savedRoleConfigs, setSavedRoleConfigs] = useState<ProjectRoleConfig[]>(defaultRoleConfigs);
  const [hasKeyPeopleChanges, setHasKeyPeopleChanges] = useState(false);
  const [isSavingKeyPeople, setIsSavingKeyPeople] = useState(false);

  // Load role configs from backend on mount (live mode only)
  useEffect(() => {
    const loadRoleConfigs = async () => {
      const mode = getDataMode();
      if (mode !== 'live') return;
      try {
        const apiConfigs = await fetchProjectRoleConfigs();
        if (apiConfigs.length > 0) {
          // Merge API response with defaults so all roles are present
          const merged: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => {
            const fromApi = apiConfigs.find((c) => c.role === role);
            if (fromApi) {
              return {
                role: fromApi.role as ProjectRoleType,
                enabled: fromApi.enabled,
                required: fromApi.required,
                displayLabel: fromApi.displayLabel ?? undefined,
              };
            }
            return { role, enabled: true, required: role === 'project-manager', displayLabel: undefined };
          });
          setRoleConfigs(merged);
          setSavedRoleConfigs(merged);
        }
      } catch (err) {
        console.error('Failed to load role configs:', err);
      }
    };
    loadRoleConfigs();
  }, []);

  const updateRoleConfig = (role: ProjectRoleType, patch: Partial<ProjectRoleConfig>) => {
    setRoleConfigs((prev) =>
      prev.map((c) => {
        if (c.role !== role) return c;
        const updated = { ...c, ...patch };
        // Required can only be true when enabled
        if (!updated.enabled) updated.required = false;
        return updated;
      })
    );
    setHasKeyPeopleChanges(true);
  };

  const handleSaveKeyPeopleSettings = async () => {
    setIsSavingKeyPeople(true);
    const mode = getDataMode();
    try {
      if (mode === 'live') {
        const configItems = roleConfigs.map((c) => ({
          role: c.role,
          enabled: c.enabled,
          required: c.required,
          displayLabel: c.displayLabel ?? null,
        }));
        await saveProjectRoleConfigs(configItems);
      }
      setSavedRoleConfigs(roleConfigs);
      setHasKeyPeopleChanges(false);
      toast.success('Key People settings saved.');
    } catch (err) {
      console.error('Failed to save role configs:', err);
      toast.error('Failed to save Key People settings.');
    } finally {
      setIsSavingKeyPeople(false);
    }
  };

  const handleDiscardKeyPeopleSettings = () => {
    setRoleConfigs(savedRoleConfigs);
    setHasKeyPeopleChanges(false);
  };

  // ===== PROJECT CONTENT FIELDS SETTINGS STATE =====
  const {
    settings: contentSettings,
    hasUnsavedChanges: hasContentChanges,
    isSaving: isSavingContent,
    updateSetting: updateContentSetting,
    saveSettings: saveContentSettings,
    resetToDefaults: resetContentSettings,
  } = useProjectContentSettings();

  // ===== PROJECT CUSTOM FIELDS (PROJECT ATTRIBUTES) STATE =====
  const {
    definitions: customFieldDefs,
    hasUnsavedChanges: hasCustomFieldChanges,
    isSaving: isSavingCustomFields,
    addDefinition,
    updateDefinition,
    removeDefinition,
    reorderDefinitions,
    saveDefinitions,
    discardChanges: discardCustomFieldChanges,
  } = useProjectCustomFields();

  // State for the "Add field" inline form
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<CustomFieldType>('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState(''); // comma-separated

  // Inline options editing for existing select fields
  const [editingOptionsId, setEditingOptionsId] = useState<string | null>(null);
  const [editingOptionsValue, setEditingOptionsValue] = useState('');

  const FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
    text: 'Text',
    number: 'Number',
    currency: 'Currency',
    date: 'Date',
    boolean: 'Yes / No',
    select: 'Dropdown',
  };

  const handleAddField = () => {
    if (!newFieldLabel.trim()) return;
    const options =
      newFieldType === 'select'
        ? newFieldOptions
            .split(',')
            .map((o) => o.trim())
            .filter(Boolean)
        : undefined;
    addDefinition({
      label: newFieldLabel.trim(),
      fieldType: newFieldType,
      required: newFieldRequired,
      enabled: true,
      placeholder: newFieldPlaceholder.trim() || undefined,
      options,
    });
    // Reset form
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldPlaceholder('');
    setNewFieldOptions('');
    setIsAddingField(false);
  };

  const handleCancelAddField = () => {
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldPlaceholder('');
    setNewFieldOptions('');
    setIsAddingField(false);
  };

  const handleMoveField = (id: string, direction: 'up' | 'down') => {
    const sorted = [...customFieldDefs].sort((a, b) => a.displayOrder - b.displayOrder);
    const idx = sorted.findIndex((d) => d.id === id);
    if (idx === -1) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === sorted.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newOrder = sorted.map((d) => d.id);
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    reorderDefinitions(newOrder);
  };

  // ===== BUDGET CONFIGURATION STATE =====
  const {
    trackingType,
    hasUnsavedChanges: hasBudgetConfigChanges,
    isSaving: isSavingBudgetConfig,
    updateTrackingType,
    saveConfiguration: saveBudgetConfiguration,
    discardChanges: discardBudgetConfigChanges,
  } = useBudgetConfiguration();
  const [showBudgetConfigSuccess, setShowBudgetConfigSuccess] = useState(false);

  // ===== BUDGET CONFIGURATION HANDLERS =====
  const handleBudgetTypeChange = (value: string) => {
    updateTrackingType(value as BudgetTrackingType);
  };

  const handleSaveBudgetConfig = async () => {
    try {
      await saveBudgetConfiguration();
      setShowBudgetConfigSuccess(true);
      toast.success('Budget configuration saved successfully');
      setTimeout(() => setShowBudgetConfigSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save budget configuration:', error);
      toast.error('Failed to save budget configuration');
    }
  };

  const handleDiscardBudgetConfig = () => {
    discardBudgetConfigChanges();
    setShowBudgetConfigSuccess(false);
    toast.info('Changes discarded');
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Project Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure project-related settings and preferences
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="border-b">
            <TabsList variant="icon" showArrows>
              <TabsTrigger variant="icon" value="project-creation" showUnderline>
                <FileEdit className="size-5" />
                Project Creation
              </TabsTrigger>
              <TabsTrigger variant="icon" value="budget-settings" showUnderline>
                <Landmark className="size-5" />
                Budget Settings
              </TabsTrigger>
              <TabsTrigger variant="icon" value="status-labels" showUnderline>
                <Pencil className="size-5" />
                Status Labels
              </TabsTrigger>
              <TabsTrigger variant="icon" value="thresholds" showUnderline>
                <TrendingUp className="size-5" />
                Metrics & Thresholds
              </TabsTrigger>
              <TabsTrigger variant="icon" value="key-people" showUnderline>
                <Users className="size-5" />
                Key People
              </TabsTrigger>
              <TabsTrigger variant="icon" value="content-fields" showUnderline>
                <LayoutList className="size-5" />
                Content Fields
              </TabsTrigger>
              <TabsTrigger variant="icon" value="custom-fields" showUnderline>
                <SlidersHorizontal className="size-5" />
                Custom Fields
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Project Creation Tab */}
          <TabsContent value="project-creation" className="mt-0">
            <ProjectCreationSetup />
          </TabsContent>

          {/* Budget Settings Tab */}
          <TabsContent value="budget-settings" className="mt-0">
            <div className="space-y-6">
              {/* Budget Configuration Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Budget Tracking Configuration</CardTitle>
                  <CardDescription>
                    Configure how budgets are tracked across all projects. This setting applies globally to Resource, Sales, and Cost budgets.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Success Message */}
                  {showBudgetConfigSuccess && (
                    <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                      <AlertCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertTitle className="text-green-800 dark:text-green-200">Configuration Saved</AlertTitle>
                      <AlertDescription className="text-green-700 dark:text-green-300">
                        Your budget tracking configuration has been updated successfully.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Unsaved Changes Warning */}
                  {hasBudgetConfigChanges && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        You have unsaved changes. Click "Save Configuration" to apply your changes.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Budget Type Selector */}
                  <BudgetTypeSelector value={trackingType} onChange={handleBudgetTypeChange} />

                  {/* Action Buttons */}
                  {hasBudgetConfigChanges && (
                    <div className="flex items-center justify-end gap-3 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={handleDiscardBudgetConfig}
                        disabled={isSavingBudgetConfig}
                      >
                        Discard Changes
                      </Button>
                      <Button
                        onClick={handleSaveBudgetConfig}
                        disabled={isSavingBudgetConfig}
                      >
                        {isSavingBudgetConfig ? 'Saving...' : 'Save Configuration'}
                      </Button>
                    </div>
                  )}

                  {/* Info Alert */}
                  <Alert variant="info">
                    <AlertDescription>
                      <strong>Global Configuration:</strong> This setting applies to all budget types (Resource, Sales, and Cost) 
                      to ensure consistent tracking and reporting across your projects.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Budget Categories Section */}
              <div>
                <div className="mb-4">
                  <h2 className="text-2xl font-bold tracking-tight">Budget Categories</h2>
                  <p className="text-muted-foreground mt-1">
                    Manage budget categories used across your projects
                  </p>
                </div>
                <CategoryManagement 
                  searchTerm={categorySearchTerm}
                  setSearchTerm={setCategorySearchTerm}
                />
              </div>

            </div>
          </TabsContent>

          {/* Status Labels Tab */}
          <TabsContent value="status-labels" className="mt-0">
            <ProjectStatusLabels />
          </TabsContent>

          {/* Thresholds Tab */}
          <TabsContent value="thresholds" className="mt-0">
            <div className="space-y-6">

              {/* Resource Coverage Thresholds */}
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">Resource Coverage Thresholds</CardTitle>
                      <CardDescription className="mt-1">
                        Set the coverage % at which a project's resourcing status turns amber or red.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-8">

                  {/* Visual legend */}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-success-500" />
                      <span className="text-sm font-medium text-success-700 dark:text-success-400">Fully Resourced</span>
                      <span className="text-sm text-muted-foreground">
                        ≥ {thresholds.fullyResourcedMin}%
                      </span>
                    </div>
                    <span className="text-muted-foreground/40 text-sm">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-warning-500" />
                      <span className="text-sm font-medium text-warning-700 dark:text-warning-400">Partially Resourced</span>
                      <span className="text-sm text-muted-foreground">
                        {thresholds.partiallyResourcedMin}–{thresholds.fullyResourcedMin - 1}%
                      </span>
                    </div>
                    <span className="text-muted-foreground/40 text-sm">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-error-500" />
                      <span className="text-sm font-medium text-error-700 dark:text-error-400">Unresourced</span>
                      <span className="text-sm text-muted-foreground">
                        &lt; {thresholds.partiallyResourcedMin}%
                      </span>
                    </div>
                  </div>

                  <Separator />

                  {/* Fully Resourced threshold */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Fully Resourced minimum</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Projects at or above this coverage % show a green "Fully Resourced" badge.
                        </p>
                      </div>
                      <Badge variant="outline" className="text-success-700 border-success-300 bg-success-50 dark:bg-success-950/20 dark:text-success-400 dark:border-success-800 tabular-nums min-w-[3.5rem] justify-center">
                        {thresholds.fullyResourcedMin}%
                      </Badge>
                    </div>
                    <Slider
                      value={[thresholds.fullyResourcedMin]}
                      onValueChange={([val]) => {
                        // Fully resourced must always be above partiallyResourcedMin + 5
                        if (val > thresholds.partiallyResourcedMin + 5 && val <= 100) {
                          updateThreshold('fullyResourcedMin', val);
                        }
                      }}
                      min={55}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>55%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* Partially Resourced threshold */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Partially Resourced minimum</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Projects below this coverage % show a red "Unresourced" badge.
                        </p>
                      </div>
                      <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[3.5rem] justify-center">
                        {thresholds.partiallyResourcedMin}%
                      </Badge>
                    </div>
                    <Slider
                      value={[thresholds.partiallyResourcedMin]}
                      onValueChange={([val]) => {
                        // Partially resourced must always be at least 5 below fullyResourcedMin
                        if (val < thresholds.fullyResourcedMin - 5 && val >= 0) {
                          updateThreshold('partiallyResourcedMin', val);
                        }
                      }}
                      min={0}
                      max={90}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0%</span>
                      <span>90%</span>
                    </div>
                  </div>

                  <Separator />

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        resetToDefaults();
                        toast.info('Thresholds reset to defaults. Save to apply.');
                      }}
                      className="gap-1.5 text-muted-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>

                    <div className="flex items-center gap-3">
                      {hasThresholdChanges && (
                        <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                      )}
                      <Button
                        onClick={async () => {
                          await saveThresholds();
                          toast.success('Coverage thresholds saved successfully.');
                        }}
                        disabled={!hasThresholdChanges || isSavingThresholds}
                        size="sm"
                      >
                        {isSavingThresholds ? 'Saving…' : 'Save thresholds'}
                      </Button>
                    </div>
                  </div>

                </CardContent>
              </Card>

              {/* Project Health Thresholds */}
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">Project Health Thresholds</CardTitle>
                      <CardDescription className="mt-1">
                        Configure the thresholds that determine a project's overall health signal — covering cost, schedule, hours, WIP/billing, and milestones. A project's health is the worst signal across all active dimensions.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-10">

                  {/* ── COLOUR LEGEND ── */}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-success-500" />
                      <span className="text-sm font-medium text-success-700 dark:text-success-400">Green</span>
                      <span className="text-sm text-muted-foreground">All dimensions healthy</span>
                    </div>
                    <span className="text-muted-foreground/40 text-sm">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-warning-500" />
                      <span className="text-sm font-medium text-warning-700 dark:text-warning-400">Amber</span>
                      <span className="text-sm text-muted-foreground">At least one dimension at amber</span>
                    </div>
                    <span className="text-muted-foreground/40 text-sm">·</span>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-error-500" />
                      <span className="text-sm font-medium text-error-700 dark:text-error-400">Red</span>
                      <span className="text-sm text-muted-foreground">At least one dimension at red</span>
                    </div>
                  </div>

                  <Separator />

                  {/* ── DIMENSION 1: COST ── */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">Cost Budget</h3>
                      <span className="text-xs text-muted-foreground">— % of total cost budget consumed</span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Amber threshold</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Cost consumed at or above this % turns amber.</p>
                        </div>
                        <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[3.5rem] justify-center">
                          {budgetThresholds.costAmberThreshold}%
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.costAmberThreshold]}
                        onValueChange={([val]) => {
                          if (val < budgetThresholds.costRedThreshold - 5 && val >= 50) {
                            updateBudgetThreshold('costAmberThreshold', val);
                          }
                        }}
                        min={50} max={95} step={5} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>50%</span><span>95%</span></div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Red threshold</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Cost consumed at or above this % turns red.</p>
                        </div>
                        <Badge variant="outline" className="text-error-700 border-error-300 bg-error-50 dark:bg-error-950/20 dark:text-error-400 dark:border-error-800 tabular-nums min-w-[3.5rem] justify-center">
                          {budgetThresholds.costRedThreshold}%
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.costRedThreshold]}
                        onValueChange={([val]) => {
                          if (val > budgetThresholds.costAmberThreshold + 5 && val <= 150) {
                            updateBudgetThreshold('costRedThreshold', val);
                          }
                        }}
                        min={80} max={150} step={5} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>80%</span><span>150%</span></div>
                    </div>
                  </div>

                  <Separator />

                  {/* ── DIMENSION 2: SCHEDULE ── */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">Schedule</h3>
                      <span className="text-xs text-muted-foreground">— completion % vs time elapsed % gap</span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Amber gap</p>
                          <p className="text-xs text-muted-foreground mt-0.5">When completion % is this many points behind time elapsed %, turns amber.</p>
                        </div>
                        <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[3.5rem] justify-center">
                          {budgetThresholds.scheduleAmberGap}%
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.scheduleAmberGap]}
                        onValueChange={([val]) => {
                          if (val < budgetThresholds.scheduleRedGap - 5 && val >= 5) {
                            updateBudgetThreshold('scheduleAmberGap', val);
                          }
                        }}
                        min={5} max={30} step={5} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>5%</span><span>30%</span></div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Red gap</p>
                          <p className="text-xs text-muted-foreground mt-0.5">When completion % is this many points behind time elapsed %, turns red.</p>
                        </div>
                        <Badge variant="outline" className="text-error-700 border-error-300 bg-error-50 dark:bg-error-950/20 dark:text-error-400 dark:border-error-800 tabular-nums min-w-[3.5rem] justify-center">
                          {budgetThresholds.scheduleRedGap}%
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.scheduleRedGap]}
                        onValueChange={([val]) => {
                          if (val > budgetThresholds.scheduleAmberGap + 5 && val <= 50) {
                            updateBudgetThreshold('scheduleRedGap', val);
                          }
                        }}
                        min={10} max={50} step={5} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>10%</span><span>50%</span></div>
                    </div>
                  </div>

                  <Separator />

                  {/* ── DIMENSION 3: HOURS ── */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">Hours</h3>
                      <span className="text-xs text-muted-foreground">— actual hours vs budget hours consumed %</span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Amber threshold</p>
                          <p className="text-xs text-muted-foreground mt-0.5">When actual hours reach this % of budget hours, turns amber.</p>
                        </div>
                        <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[3.5rem] justify-center">
                          {budgetThresholds.hoursAmberGap}%
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.hoursAmberGap]}
                        onValueChange={([val]) => {
                          if (val < budgetThresholds.hoursRedGap - 5 && val >= 50) {
                            updateBudgetThreshold('hoursAmberGap', val);
                          }
                        }}
                        min={50} max={95} step={5} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>50%</span><span>95%</span></div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Red threshold</p>
                          <p className="text-xs text-muted-foreground mt-0.5">When actual hours reach this % of budget hours, turns red.</p>
                        </div>
                        <Badge variant="outline" className="text-error-700 border-error-300 bg-error-50 dark:bg-error-950/20 dark:text-error-400 dark:border-error-800 tabular-nums min-w-[3.5rem] justify-center">
                          {budgetThresholds.hoursRedGap}%
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.hoursRedGap]}
                        onValueChange={([val]) => {
                          if (val > budgetThresholds.hoursAmberGap + 5 && val <= 150) {
                            updateBudgetThreshold('hoursRedGap', val);
                          }
                        }}
                        min={80} max={150} step={5} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>80%</span><span>150%</span></div>
                    </div>
                  </div>

                  <Separator />

                  {/* ── DIMENSION 4: WIP / BILLING ── */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">WIP / Billing</h3>
                      <span className="text-xs text-muted-foreground">— unbilled WIP amount thresholds</span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Amber threshold</p>
                          <p className="text-xs text-muted-foreground mt-0.5">When WIP balance exceeds this amount, turns amber.</p>
                        </div>
                        <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[4.5rem] justify-center">
                          £{budgetThresholds.wipAmberThreshold.toLocaleString()}
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.wipAmberThreshold]}
                        onValueChange={([val]) => {
                          if (val < budgetThresholds.wipRedThreshold - 5000) {
                            updateBudgetThreshold('wipAmberThreshold', val);
                          }
                        }}
                        min={0} max={95000} step={5000} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>£0</span><span>£95,000</span></div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Red threshold</p>
                          <p className="text-xs text-muted-foreground mt-0.5">When WIP balance exceeds this amount, turns red.</p>
                        </div>
                        <Badge variant="outline" className="text-error-700 border-error-300 bg-error-50 dark:bg-error-950/20 dark:text-error-400 dark:border-error-800 tabular-nums min-w-[4.5rem] justify-center">
                          £{budgetThresholds.wipRedThreshold.toLocaleString()}
                        </Badge>
                      </div>
                      <Slider
                        value={[budgetThresholds.wipRedThreshold]}
                        onValueChange={([val]) => {
                          if (val > budgetThresholds.wipAmberThreshold + 5000) {
                            updateBudgetThreshold('wipRedThreshold', val);
                          }
                        }}
                        min={10000} max={200000} step={5000} className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground"><span>£10,000</span><span>£200,000</span></div>
                    </div>
                  </div>

                  <Separator />

                  {/* ── DIMENSION 5: MILESTONES ── */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">Milestones</h3>
                      <span className="text-xs text-muted-foreground">— overdue milestone behaviour</span>
                    </div>

                    <div
                      onClick={() => updateBudgetThreshold('milestoneRedOnAnyOverdue', !budgetThresholds.milestoneRedOnAnyOverdue)}
                      className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                        budgetThresholds.milestoneRedOnAnyOverdue
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                        budgetThresholds.milestoneRedOnAnyOverdue ? 'border-primary' : 'border-muted-foreground/40'
                      }`}>
                        {budgetThresholds.milestoneRedOnAnyOverdue && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium leading-none">Turn red on any overdue milestone</p>
                        <p className="text-xs text-muted-foreground">If any milestone is past its due date, the project health turns red regardless of other dimensions.</p>
                      </div>
                    </div>

                    <div
                      onClick={() => updateBudgetThreshold('milestoneRedOnAnyOverdue', !budgetThresholds.milestoneRedOnAnyOverdue)}
                      className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                        !budgetThresholds.milestoneRedOnAnyOverdue
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                        !budgetThresholds.milestoneRedOnAnyOverdue ? 'border-primary' : 'border-muted-foreground/40'
                      }`}>
                        {!budgetThresholds.milestoneRedOnAnyOverdue && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium leading-none flex items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
                          Ignore milestone status in health calculation
                        </p>
                        <p className="text-xs text-muted-foreground">Overdue milestones do not affect the project health badge.</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* ── DIMENSION 6: GROSS MARGIN ── */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold">Gross Margin</h3>
                        <span className="text-xs text-muted-foreground">— actual GM% thresholds</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="margin-enabled-toggle" className="text-xs text-muted-foreground cursor-pointer">
                          {budgetThresholds.marginEnabled ? 'Enabled' : 'Disabled'}
                        </Label>
                        <Switch
                          id="margin-enabled-toggle"
                          checked={budgetThresholds.marginEnabled}
                          onCheckedChange={(checked) => updateBudgetThreshold('marginEnabled', checked)}
                        />
                      </div>
                    </div>

                    {!budgetThresholds.marginEnabled && (
                      <p className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                        Gross Margin is excluded from the health rollup. Enable once nominal code mapping is configured on the Cost Category Mapping settings page.
                      </p>
                    )}

                    <div className={budgetThresholds.marginEnabled ? '' : 'opacity-40 pointer-events-none'}>
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">Amber threshold</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                When actual GM% falls below this value, turns amber.
                              </p>
                            </div>
                            <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50 dark:bg-warning-950/20 dark:text-warning-400 dark:border-warning-800 tabular-nums min-w-[3.5rem] justify-center">
                              {budgetThresholds.marginAmberThreshold}%
                            </Badge>
                          </div>
                          <Slider
                            value={[budgetThresholds.marginAmberThreshold]}
                            onValueChange={([val]) => {
                              if (val > budgetThresholds.marginRedThreshold + 2 && val <= 50) {
                                updateBudgetThreshold('marginAmberThreshold', val);
                              }
                            }}
                            min={5} max={50} step={1} className="w-full"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground"><span>5%</span><span>50%</span></div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">Red threshold</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                When actual GM% falls below this value, turns red.
                              </p>
                            </div>
                            <Badge variant="outline" className="text-error-700 border-error-300 bg-error-50 dark:bg-error-950/20 dark:text-error-400 dark:border-error-800 tabular-nums min-w-[3.5rem] justify-center">
                              {budgetThresholds.marginRedThreshold}%
                            </Badge>
                          </div>
                          <Slider
                            value={[budgetThresholds.marginRedThreshold]}
                            onValueChange={([val]) => {
                              if (val < budgetThresholds.marginAmberThreshold - 2 && val >= 0) {
                                updateBudgetThreshold('marginRedThreshold', val);
                              }
                            }}
                            min={0} max={45} step={1} className="w-full"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground"><span>0%</span><span>45%</span></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* ── ACTIONS ── */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        resetBudgetThresholds();
                        toast.info('Project health thresholds reset to defaults. Save to apply.');
                      }}
                      className="gap-1.5 text-muted-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>

                    <div className="flex items-center gap-3">
                      {hasBudgetThresholdChanges && (
                        <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                      )}
                      <Button
                        onClick={async () => {
                          const snapshot = { ...budgetThresholds };
                          await saveBudgetThresholds();
                          appendAuditEntry({
                            changedBy,
                            category: 'Settings',
                            action: 'Updated',
                            entityName: 'Project Health Thresholds',
                            oldValue: `Cost amber ${snapshot.costAmberThreshold}%, red ${snapshot.costRedThreshold}%`,
                            newValue: `Cost amber ${budgetThresholds.costAmberThreshold}%, red ${budgetThresholds.costRedThreshold}%; Schedule amber ${budgetThresholds.scheduleAmberGap}%, red ${budgetThresholds.scheduleRedGap}%; Hours amber ${budgetThresholds.hoursAmberGap}%, red ${budgetThresholds.hoursRedGap}%; WIP amber £${budgetThresholds.wipAmberThreshold}, red £${budgetThresholds.wipRedThreshold}; Milestones red on overdue: ${budgetThresholds.milestoneRedOnAnyOverdue}`,
                            detail: 'Updated via Project Settings > Thresholds tab',
                          });
                          toast.success('Project health thresholds saved successfully.');
                        }}
                        disabled={!hasBudgetThresholdChanges || isSavingBudgetThresholds}
                        size="sm"
                      >
                        {isSavingBudgetThresholds ? 'Saving…' : 'Save thresholds'}
                      </Button>
                    </div>
                  </div>

                </CardContent>
              </Card>

              {/* Utilisation Rate Formula */}
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    <BarChart2 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">Utilisation Rate Formula</CardTitle>
                      <CardDescription className="mt-1">
                        Choose how Utilisation Rate is calculated across all projects.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    {utilisationFormulaOptions.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => handleUtilisationFormulaChange(option.value)}
                        className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                          utilisationFormula === option.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/40'
                        }`}
                      >
                        <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                          utilisationFormula === option.value
                            ? 'border-primary'
                            : 'border-muted-foreground/40'
                        }`}>
                          {utilisationFormula === option.value && (
                            <div className="h-2 w-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium leading-none">{option.label}</p>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="flex items-center justify-end gap-3">
                    {hasUnsavedUtilisationChanges && (
                      <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                    )}
                    <Button
                      onClick={handleSaveUtilisationFormula}
                      disabled={!hasUnsavedUtilisationChanges || isSavingUtilisation}
                      size="sm"
                    >
                      {isSavingUtilisation ? 'Saving…' : 'Save formula'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          {/* Key People Tab */}
          <TabsContent value="key-people" className="mt-0">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">Key People Role Configuration</CardTitle>
                      <CardDescription className="mt-1">
                        Control which accountability roles are visible on projects, whether they are required, and customise their display labels.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-0 divide-y">
                  {/* Column headings */}
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center pb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <span>Role</span>
                    <span className="w-20 text-center">Enabled</span>
                    <span className="w-20 text-center">Required</span>
                    <span className="w-44">Display Label</span>
                  </div>

                  {roleConfigs.map((config) => {
                    const defaultLabel = PROJECT_ROLE_LABELS[config.role];
                    // TODO [ENGINEERING — Devin]: The warning banner count below is hardcoded to 0.
                    // Replace with a value from GET /api/projects/role-assignment-counts
                    // which returns Record<ProjectRoleType, number>.
                    // Show the banner only when count > 0 and enabled is being toggled off.
                    const activeAssignmentCount: number = 0; // TODO [ENGINEERING — Devin]: replace with API value
                    return (
                      <div key={config.role} className="grid grid-cols-[1fr_auto_auto_auto] gap-4 items-start py-4">
                        {/* Role name + warning */}
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{defaultLabel}</p>
                          {!config.enabled && activeAssignmentCount > 0 && (
                            <p className="text-xs text-warning-600 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {activeAssignmentCount} project{activeAssignmentCount !== 1 ? 's have' : ' has'} {defaultLabel} assignments — hidden but not deleted.
                            </p>
                          )}
                        </div>

                        {/* Enabled toggle */}
                        <div className="w-20 flex justify-center pt-0.5">
                          <Switch
                            checked={config.enabled}
                            onCheckedChange={(checked) => updateRoleConfig(config.role, { enabled: checked })}
                            disabled={config.role === 'project-manager'}
                            aria-label={`${defaultLabel} enabled`}
                          />
                        </div>

                        {/* Required toggle */}
                        <div className="w-20 flex justify-center pt-0.5">
                          <Switch
                            checked={config.required}
                            onCheckedChange={(checked) => updateRoleConfig(config.role, { required: checked })}
                            disabled={!config.enabled}
                            aria-label={`${defaultLabel} required`}
                          />
                        </div>

                        {/* Display label override */}
                        <div className="w-44">
                          <Input
                            value={config.displayLabel ?? ''}
                            onChange={(e) =>
                              updateRoleConfig(config.role, {
                                displayLabel: e.target.value || undefined,
                              })
                            }
                            placeholder={defaultLabel}
                            disabled={!config.enabled}
                            className="h-8 text-sm"
                            aria-label={`${defaultLabel} display label`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Info note */}
              <Alert variant="info">
                <AlertDescription>
                  <strong>Project Manager is always enabled and required.</strong> All other roles are optional.
                  Disabling a role hides it from all projects but preserves existing assignments — re-enabling it restores visibility.
                </AlertDescription>
              </Alert>

              {/* Save / Discard */}
              {hasKeyPeopleChanges && (
                <div className="flex items-center justify-end gap-3">
                  <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                  <Button variant="outline" onClick={handleDiscardKeyPeopleSettings} disabled={isSavingKeyPeople}>
                    Discard Changes
                  </Button>
                  <Button onClick={handleSaveKeyPeopleSettings} disabled={isSavingKeyPeople}>
                    {isSavingKeyPeople ? 'Saving…' : 'Save Settings'}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Content Fields Tab */}
          <TabsContent value="content-fields" className="mt-0">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <LayoutList className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">Project Content Fields</CardTitle>
                      <CardDescription className="mt-1">
                        Control which narrative content cards appear on the Project Overview tab. Hidden cards are not deleted — their content is preserved and will reappear if re-enabled.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-0 divide-y">
                  {/* Column headings */}
                  <div className="grid grid-cols-[1fr_auto] gap-4 items-center pb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <span>Field</span>
                    <span className="w-20 text-center">Visible</span>
                  </div>

                  {/* Objectives row */}
                  <div className="grid grid-cols-[1fr_auto] gap-4 items-start py-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Project Objectives</p>
                      <p className="text-xs text-muted-foreground">
                        Why the project exists — strategic goals, success criteria, and what "done well" looks like.
                      </p>
                    </div>
                    <div className="w-20 flex justify-center pt-0.5">
                      <Switch
                        checked={contentSettings.showObjectives}
                        onCheckedChange={(checked) => updateContentSetting('showObjectives', checked)}
                        aria-label="Show Project Objectives"
                      />
                    </div>
                  </div>

                  {/* Key Deliverables row */}
                  <div className="grid grid-cols-[1fr_auto] gap-4 items-start py-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Key Deliverables</p>
                      <p className="text-xs text-muted-foreground">
                        What the project produces — tangible outputs, artefacts, systems, or services being delivered.
                      </p>
                    </div>
                    <div className="w-20 flex justify-center pt-0.5">
                      <Switch
                        checked={contentSettings.showKeyDeliverables}
                        onCheckedChange={(checked) => updateContentSetting('showKeyDeliverables', checked)}
                        aria-label="Show Key Deliverables"
                      />
                    </div>
                  </div>

                  {/* Project Notes row */}
                  <div className="grid grid-cols-[1fr_auto] gap-4 items-start py-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Project Notes</p>
                      <p className="text-xs text-muted-foreground">
                        Running PM context — decisions made, client nuances, escalation history, and handover information.
                      </p>
                    </div>
                    <div className="w-20 flex justify-center pt-0.5">
                      <Switch
                        checked={contentSettings.showNotes}
                        onCheckedChange={(checked) => updateContentSetting('showNotes', checked)}
                        aria-label="Show Project Notes"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Info note */}
              <Alert variant="info">
                <AlertDescription>
                  <strong>Content is never deleted when a field is hidden.</strong> All existing Objectives, Deliverables, and Notes are preserved. Re-enabling a field instantly restores visibility across all projects.
                  {/* TODO [ENGINEERING — Devin]: Wire to PATCH /api/company/project-content-settings on save.
                      GET /api/company/project-content-settings on mount to load persisted settings.
                      Remove the localStorage fallback in lib/project-content-settings-storage.ts once the API is live. */}
                </AlertDescription>
              </Alert>

              {/* Reset + Save / Discard footer */}
              {hasContentChanges && (
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      resetContentSettings();
                    }}
                    className="gap-1.5 text-muted-foreground"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reset to defaults
                  </Button>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetContentSettings()}
                      disabled={isSavingContent}
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        await saveContentSettings();
                        toast.success('Content field visibility saved.');
                      }}
                      disabled={isSavingContent}
                    >
                      {isSavingContent ? 'Saving…' : 'Save Settings'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Custom Fields Tab */}
          <TabsContent value="custom-fields" className="mt-0">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <CardTitle className="text-base">Project Attributes — Custom Fields</CardTitle>
                        <CardDescription className="mt-1">
                          Define typed custom fields that appear on the Project Attributes card on every project's Overview tab.
                          Fields with no value are hidden in read view. Disabling a field hides it but preserves all stored values.
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs font-normal">
                      {customFieldDefs.filter((d) => d.enabled).length} / {MAX_FIELDS} fields used
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Field list */}
                  {customFieldDefs.length === 0 && !isAddingField ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/25 p-10 text-center text-muted-foreground gap-2">
                      <SlidersHorizontal className="h-8 w-8 opacity-30" />
                      <p className="text-sm font-medium">No custom fields defined yet</p>
                      <p className="text-xs">Add your first field to start capturing project-specific attributes.</p>
                    </div>
                  ) : (
                    <div className="divide-y rounded-lg border">
                      {/* Column headings */}
                      <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 items-center px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide bg-muted/40">
                        <span className="w-8" />
                        <span>Label &amp; Type</span>
                        <span className="w-20 text-center">Required</span>
                        <span className="w-20 text-center">Enabled</span>
                        <span className="w-16 text-center">Order</span>
                        <span className="w-8" />
                      </div>

                      {[...customFieldDefs]
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((def, idx, arr) => (
                          <div
                            key={def.id}
                            className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 items-center px-4 py-3"
                          >
                            {/* Drag handle placeholder */}
                            <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />

                            {/* Label + type badge */}
                            <div className="min-w-0 space-y-0.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Input
                                  value={def.label}
                                  onChange={(e) =>
                                    updateDefinition(def.id, { label: e.target.value })
                                  }
                                  className="h-7 text-sm w-40"
                                  aria-label={`Label for field ${def.label}`}
                                />
                                <Badge variant="outline" className="text-xs font-normal shrink-0">
                                  {FIELD_TYPE_LABELS[def.fieldType]}
                                </Badge>
                                {def.required && (
                                  <Badge variant="destructive" className="text-xs font-normal shrink-0 bg-destructive/10 text-destructive border-destructive/20">
                                    Required
                                  </Badge>
                                )}
                              </div>
                              {def.placeholder && (
                                <p className="text-xs text-muted-foreground truncate">
                                  Hint: {def.placeholder}
                                </p>
                              )}
                              {def.fieldType === 'select' && (
                                editingOptionsId === def.id ? (
                                  /* ── Inline options editor ── */
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <Input
                                      autoFocus
                                      value={editingOptionsValue}
                                      onChange={(e) => setEditingOptionsValue(e.target.value)}
                                      placeholder="Option A, Option B, Option C"
                                      className="h-7 text-xs flex-1"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          const opts = editingOptionsValue.split(',').map((o) => o.trim()).filter(Boolean);
                                          updateDefinition(def.id, { options: opts });
                                          setEditingOptionsId(null);
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingOptionsId(null);
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const opts = editingOptionsValue.split(',').map((o) => o.trim()).filter(Boolean);
                                        updateDefinition(def.id, { options: opts });
                                        setEditingOptionsId(null);
                                      }}
                                      className="p-1 rounded text-primary hover:bg-primary/10 transition-colors"
                                      aria-label="Save options"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingOptionsId(null)}
                                      className="p-1 rounded text-muted-foreground hover:bg-muted transition-colors"
                                      aria-label="Cancel editing options"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  /* ── Read display ── */
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingOptionsId(def.id);
                                      setEditingOptionsValue((def.options ?? []).join(', '));
                                    }}
                                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground group mt-0.5"
                                    aria-label={`Edit options for ${def.label}`}
                                  >
                                    <span className="truncate">
                                      {def.options && def.options.length > 0
                                        ? `Options: ${def.options.join(', ')}`
                                        : 'No options set — click to add'}
                                    </span>
                                    <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
                                  </button>
                                )
                              )}
                            </div>

                            {/* Required toggle */}
                            <div className="w-20 flex justify-center">
                              <Switch
                                checked={def.required}
                                onCheckedChange={(checked) =>
                                  updateDefinition(def.id, { required: checked })
                                }
                                disabled={!def.enabled}
                                aria-label={`${def.label} required`}
                              />
                            </div>

                            {/* Enabled toggle */}
                            <div className="w-20 flex justify-center">
                              <Switch
                                checked={def.enabled}
                                onCheckedChange={(checked) =>
                                  updateDefinition(def.id, {
                                    enabled: checked,
                                    // If disabling, also clear required
                                    required: checked ? def.required : false,
                                  })
                                }
                                aria-label={`${def.label} enabled`}
                              />
                            </div>

                            {/* Move up / down */}
                            <div className="w-16 flex items-center justify-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => handleMoveField(def.id, 'up')}
                                disabled={idx === 0}
                                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Move ${def.label} up`}
                              >
                                <ChevronUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleMoveField(def.id, 'down')}
                                disabled={idx === arr.length - 1}
                                className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={`Move ${def.label} down`}
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                            </div>

                            {/* Delete */}
                            <div className="w-8 flex justify-center">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    type="button"
                                    className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                    aria-label={`Delete ${def.label}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete "{def.label}"?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently remove the field definition and{' '}
                                      <strong>all stored values across every project</strong>.
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => removeDefinition(def.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete field
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Add field form */}
                  {isAddingField ? (
                    <div className="rounded-lg border border-info-200 bg-info-50/30 p-4 space-y-4">
                      <p className="text-sm font-medium">New field</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {/* Label */}
                        <div className="space-y-1.5">
                          <Label htmlFor="new-field-label" className="text-xs">
                            Label <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="new-field-label"
                            value={newFieldLabel}
                            onChange={(e) => setNewFieldLabel(e.target.value)}
                            placeholder="e.g. Contract Reference"
                            className="h-8 text-sm"
                            autoFocus
                          />
                        </div>

                        {/* Field type */}
                        <div className="space-y-1.5">
                          <Label htmlFor="new-field-type" className="text-xs">Field Type</Label>
                          <Select
                            value={newFieldType}
                            onValueChange={(v) => setNewFieldType(v as CustomFieldType)}
                          >
                            <SelectTrigger id="new-field-type" className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(FIELD_TYPE_LABELS) as CustomFieldType[]).map((t) => (
                                <SelectItem key={t} value={t} className="text-sm">
                                  {FIELD_TYPE_LABELS[t]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Placeholder */}
                        <div className="space-y-1.5">
                          <Label htmlFor="new-field-placeholder" className="text-xs">
                            Placeholder / hint text
                          </Label>
                          <Input
                            id="new-field-placeholder"
                            value={newFieldPlaceholder}
                            onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                            placeholder="Optional hint shown in edit mode"
                            className="h-8 text-sm"
                          />
                        </div>

                        {/* Options — only for select */}
                        {newFieldType === 'select' && (
                          <div className="space-y-1.5">
                            <Label htmlFor="new-field-options" className="text-xs">
                              Dropdown options <span className="text-muted-foreground">(comma-separated)</span>
                            </Label>
                            <Input
                              id="new-field-options"
                              value={newFieldOptions}
                              onChange={(e) => setNewFieldOptions(e.target.value)}
                              placeholder="e.g. Option A, Option B, Option C"
                              className="h-8 text-sm"
                            />
                          </div>
                        )}
                      </div>

                      {/* Required toggle */}
                      <div className="flex items-center gap-2">
                        <Switch
                          id="new-field-required"
                          checked={newFieldRequired}
                          onCheckedChange={setNewFieldRequired}
                        />
                        <Label htmlFor="new-field-required" className="text-sm font-normal cursor-pointer">
                          Required field
                        </Label>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1">
                        <Button variant="outline" size="sm" onClick={handleCancelAddField}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleAddField}
                          disabled={!newFieldLabel.trim()}
                        >
                          Add Field
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingField(true)}
                      disabled={customFieldDefs.length >= MAX_FIELDS}
                      className="gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add field
                      {customFieldDefs.length >= MAX_FIELDS && (
                        <span className="text-muted-foreground ml-1">(limit reached)</span>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Info note */}
              <Alert variant="info">
                <AlertDescription>
                  <strong>Field type cannot be changed once a field has been saved.</strong>{' '}
                  To change a field's type, disable it and create a new field. Disabling a field hides it from all projects but preserves all stored values — re-enabling it restores visibility instantly.
                  {/* TODO [ENGINEERING — Devin]: Wire field type lock to API — once any project has
                      a non-empty value for a field's id, the fieldType selector should be disabled
                      with a tooltip: "Field type cannot be changed once values have been recorded." */}
                </AlertDescription>
              </Alert>

              {/* Save / Discard footer */}
              {hasCustomFieldChanges && (
                <div className="flex items-center justify-end gap-3">
                  <p className="text-xs text-muted-foreground">You have unsaved changes</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      discardCustomFieldChanges();
                      toast.info('Changes discarded.');
                    }}
                    disabled={isSavingCustomFields}
                  >
                    Discard Changes
                  </Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await saveDefinitions();
                        toast.success('Custom field definitions saved.');
                      } catch (err) {
                        console.error('Failed to save custom field definitions:', err);
                        toast.error('Failed to save custom field definitions. Please try again.');
                      }
                    }}
                    disabled={isSavingCustomFields}
                  >
                    {isSavingCustomFields ? 'Saving…' : 'Save Settings'}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
